import React, {useCallback, useEffect, useState} from "react";
import {Button, Card, ProgressBar} from "react-bootstrap";
import axios from "axios";
import {router} from "@inertiajs/react";
import {FlexToast, useExternalControls} from "laravel-inertia-backend";
import {campaignStatusLabel} from "./campaignStatusLabels.js";

const ACTIVE_STATUSES = ["scheduled", "sending"];

/**
 * Versand-Steuerung + Fortschrittsanzeige für eine Kampagne (Slice 4).
 * Pollt /campaigns/{id}/progress per Intervall, solange die Kampagne aktiv
 * ist (scheduled/sending) - der eigentliche Versand läuft asynchron über
 * campaigns:dispatch-due + CampaignSendJob, dieses Panel setzt nur den
 * Kampagnen-Status, den der Scheduler dann aufgreift.
 */
export default function SendControlPanel({campaign, disableStart = false, disableStartReason}) {
    const {addToast} = useExternalControls();
    const [progress, setProgress] = useState(null);
    const [busy, setBusy] = useState(false);

    const fetchProgress = useCallback(async () => {
        if (!campaign?.id) {
            return;
        }
        try {
            const {data} = await axios.get(`/campaigns/${campaign.id}/progress`);
            setProgress(data);
        } catch (e) {
            // Polling-Fehler bewusst still - kein Toast-Spam bei kurzzeitigen Netzwerkproblemen.
        }
    }, [campaign?.id]);

    useEffect(() => {
        fetchProgress();
        const isActive = ACTIVE_STATUSES.includes(campaign?.status);
        if (!isActive) {
            return undefined;
        }
        const interval = setInterval(fetchProgress, 5000);
        return () => clearInterval(interval);
    }, [fetchProgress, campaign?.status]);

    const runAction = async (action, successMessage) => {
        setBusy(true);
        try {
            await axios.post(`/campaigns/${campaign.id}/${action}`);
            addToast(<FlexToast title="Versand" autohide={true} message={successMessage}/>);
            await fetchProgress();
            router.reload({only: ["campaign"]});
        } catch (e) {
            const message = e.response?.data?.message
                || Object.values(e.response?.data?.errors || {})[0]?.[0]
                || e.message
                || "Aktion fehlgeschlagen";
            addToast(<FlexToast variant="danger" title="Versand" autohide={false} message={message}/>);
        } finally {
            setBusy(false);
        }
    };

    if (!campaign?.id) {
        return null;
    }

    const status = campaign.status;
    const isPaused = !!campaign.is_paused;
    const total = progress?.total ?? 0;
    const sent = progress?.sent ?? 0;
    const failed = progress?.failed ?? 0;
    const percent = total > 0 ? Math.round((sent / total) * 100) : 0;

    const canStart = (status === "draft" || status === "canceled") && !disableStart;
    const canPause = ACTIVE_STATUSES.includes(status) && !isPaused;
    const canResume = ACTIVE_STATUSES.includes(status) && isPaused;
    const canStop = ACTIVE_STATUSES.includes(status) || status === "paused";

    return (
        <Card className="mb-3">
            <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                        <strong>Versand-Status:</strong>{" "}
                        {campaignStatusLabel(status)}
                        {isPaused && ACTIVE_STATUSES.includes(status) ? " (pausiert)" : ""}
                    </div>
                    <div className="d-flex gap-2">
                        <Button size="sm" variant="primary" disabled={busy || !canStart}
                                title={!canStart && disableStart ? disableStartReason : undefined}
                                onClick={() => runAction("start", "Versand gestartet.")}>
                            Start
                        </Button>
                        <Button size="sm" variant="warning" disabled={busy || !canPause}
                                onClick={() => runAction("pause", "Versand pausiert.")}>
                            Pause
                        </Button>
                        <Button size="sm" variant="success" disabled={busy || !canResume}
                                onClick={() => runAction("resume", "Versand fortgesetzt.")}>
                            Fortsetzen
                        </Button>
                        <Button size="sm" variant="danger" disabled={busy || !canStop}
                                onClick={() => runAction("stop", "Versand abgebrochen.")}>
                            Stopp
                        </Button>
                    </div>
                </div>

                {progress && (
                    <>
                        <ProgressBar now={percent} label={`${percent}%`} className="mb-2"/>
                        <div className="text-muted small">
                            {sent} gesendet, {failed} fehlgeschlagen, {progress.queued} eingeplant,{" "}
                            {progress.pending} offen von {total} gesamt.
                        </div>
                    </>
                )}
            </Card.Body>
        </Card>
    );
}
